import { type ReactNode, useRef } from "react";

const SWIPE_THRESHOLD = 50;
const BUTTON_WIDTH = 72;

type SwipeToDeleteProps = {
  onDelete: () => void;
  children: ReactNode;
};

export const SwipeToDelete = ({ onDelete, children }: SwipeToDeleteProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const currentX = useRef(0);
  const isOpenRef = useRef(false);
  const isDragging = useRef(false);
  const isHorizontal = useRef<boolean | null>(null);
  const didEmitCancel = useRef(false);

  /** DOM 直接操作で transform を更新（再レンダリング不要） */
  const applyTransform = (x: number, animate: boolean) => {
    const el = contentRef.current;
    if (!el) return;
    el.style.transition = animate ? "transform 200ms ease-out" : "none";
    el.style.transform = `translateX(${x}px)`;
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (document.body.dataset.dragging) return;

    const touch = e.touches[0];
    startX.current = touch.clientX;
    startY.current = touch.clientY;
    isDragging.current = true;
    isHorizontal.current = null;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current) return;
    if (document.body.dataset.dragging) {
      isDragging.current = false;
      return;
    }

    const touch = e.touches[0];
    const diffX = touch.clientX - startX.current;
    const diffY = touch.clientY - startY.current;

    // 最初の移動で方向を判定
    if (isHorizontal.current === null) {
      if (Math.abs(diffX) < 5 && Math.abs(diffY) < 5) return;
      isHorizontal.current = Math.abs(diffX) > Math.abs(diffY);
      if (!isHorizontal.current) {
        isDragging.current = false;
        return;
      }
      document.body.dataset.swiping = "true";
      // dnd-kit の pending activation をキャンセルする
      didEmitCancel.current = true;
      e.nativeEvent.target?.dispatchEvent(
        new Event("touchcancel", { bubbles: true }),
      );
    }

    const newX = isOpenRef.current
      ? Math.min(0, Math.max(-BUTTON_WIDTH, diffX - BUTTON_WIDTH))
      : Math.min(0, Math.max(-BUTTON_WIDTH, diffX));

    currentX.current = newX;
    applyTransform(newX, false);
  };

  const snapToPosition = () => {
    // 自前で発火した touchcancel は無視する
    if (didEmitCancel.current) {
      didEmitCancel.current = false;
      return;
    }
    isDragging.current = false;
    delete document.body.dataset.swiping;

    if (isHorizontal.current === null) return;

    const x = currentX.current;

    if (isOpenRef.current) {
      if (x > -SWIPE_THRESHOLD) {
        isOpenRef.current = false;
        currentX.current = 0;
        applyTransform(0, true);
      } else {
        currentX.current = -BUTTON_WIDTH;
        applyTransform(-BUTTON_WIDTH, true);
      }
    } else {
      if (x < -SWIPE_THRESHOLD) {
        isOpenRef.current = true;
        currentX.current = -BUTTON_WIDTH;
        applyTransform(-BUTTON_WIDTH, true);
      } else {
        currentX.current = 0;
        applyTransform(0, true);
      }
    }
  };

  return (
    <div
      className="relative overflow-hidden"
      data-swipe-container
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={snapToPosition}
      onTouchCancel={snapToPosition}
    >
      <button
        type="button"
        aria-label="削除"
        className="absolute right-0 top-0 flex h-full items-center justify-center bg-destructive text-sm font-medium text-destructive-foreground"
        style={{ width: BUTTON_WIDTH }}
        onClick={onDelete}
      >
        削除
      </button>
      <div
        ref={contentRef}
        data-swipe-content
        className="relative bg-background"
      >
        {children}
      </div>
    </div>
  );
};
